import React, { useEffect, useState } from "react";
import { Bar, Line, Pie, Doughnut, Radar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Title, Tooltip, Legend, Filler, RadialLinearScale } from "chart.js";
import { useAuth } from "../../../context/auth-context";
import axios from "axios";
import { formatToZMW, rootUrl, sumOfArray } from "../../../helpers";
import { toast } from "react-toastify";
import { FiArrowUp, FiArrowDown, FiDollarSign, FiTrendingUp, FiActivity } from "react-icons/fi";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

ChartJS.register(
  RadialLinearScale,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const Dashboards = () => {
  const auth = useAuth();
  const [dash, setDash] = useState([]);
  const [cht, setCht] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState([new Date(new Date().setMonth(new Date().getMonth() - 1)), new Date()]);
  const [startDate, endDate] = dateRange;

  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  const groupByDaysWithType = (data) => {
    if (!data) return [];
    const days = {};
  
    data.forEach(item => {
      const date = new Date(item.date);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      // Group by the exact date in YYYY-MM-DD format
      const dayKey = `${year}-${month}-${day}`;
  
      if (!days[dayKey]) {
        days[dayKey] = { totalIncome: 0, totalExpense: 0, items: [] };
      }
  
      if (item.type === 'income') {
        days[dayKey].totalIncome += parseFloat(item.amount);
      } else if (item.type === 'expense') {
        days[dayKey].totalExpense += parseFloat(item.amount);
      }
      days[dayKey].items.push(item);
    });
  
    return Object.entries(days).map(([day, data]) => ({
      day,
      ...data,
      netProfit: data.totalIncome - data.totalExpense
    }));
  };
  

  const fetchData = async () => {
    try {
      setLoading(true);
      const resp = await axios.get(rootUrl(`/getDash/${auth.user.id}`));
      setDash(resp.data.data || []);
      setCht(groupByDaysWithType(resp.data.data || []));
      setLoading(false);
    } catch (e) {
      toast.error(e?.message || "Failed to fetch data");
      setDash([]);
      setCht([]);
    } 
  };

  useEffect(() => {
      fetchData();
  }, []);

  // useEffect(()=>{
    
  //   if(dash.length > 0 && cht.length > 0){
  //     setLoading(false);
  //   }

  // }, [dash, cht])

  const getGradient = (ctx, color1, color2) => {
    const gradient = ctx.createLinearGradient(0, 0, 0, 400);
    gradient.addColorStop(0, color1);
    gradient.addColorStop(1, color2);
    return gradient;
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          label: (context) => ` ${context.dataset.label}: ${formatToZMW(context.raw)}`
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { callback: (value) => formatToZMW(value) }
      }
    }
  };

  const invoiceData = {
    labels: cht.map((res, index)=>++index),
    datasets: [
      {
        label: "Receipts",
        data: cht.map((res)=>res.totalIncome),
        backgroundColor: ['#3b82f6', '#10b981', '#f59e0b'],
        borderColor: "#FFFFFF",
        borderWidth: 1,
        borderWidth: 2,
        borderRadius: 8,
        fill: true
      },
    ],
  };
  
  const paymentsData = {
    labels: cht.map((res, index)=>++index),
    datasets: [
      {
        label: "Payments",
        data: cht.map((res)=>res.totalExpense),
        borderColor: "#10b981",
        backgroundColor: "rgba(16, 185, 129, 0.2)",
        borderWidth: 3,
        tension: 0.4,
        pointRadius: 5,
        pointBackgroundColor: '#10b981',
        fill: true
      },
    ],
  };

  const netProfitData = {
    labels: cht.map((res, index)=>++index),
    datasets: [
      {
        label: cht?.map(res => res.netProfit >= 0 ? 'Profit' : 'Loss'),
        data: cht.map((res)=>res.netProfit),
        borderColor: "#FFFFFF",
        backgroundColor: cht?.map(res => res.netProfit >= 0 ? '#10b981' : '#ef4444'),
        borderWidth: 3,
        tension: 0.4,
        pointRadius: 5,
        pointBackgroundColor: '#10b981',
        fill: true
      },
    ],
  };


  const uniqueLabels = [...new Set(dash.map(res => res.type))];

const typeTotals = dash.reduce((acc, { type, amount }) => {
  acc[type] = (acc[type] || 0) + parseFloat(amount);
  return acc;
}, {});


const dataValues = uniqueLabels.map(type => typeTotals[type]);


const expenseData = {
  labels: uniqueLabels,
  datasets: [
    {
      label: "Income/Expense",
      data: dataValues, // Ensure data follows labels order
      backgroundColor: ['#3b82f6', '#10b981', '#f59e0b'], // Add more colors if needed
      hoverOffset: 8
    }
  ]
};


  const KpiCard = ({ title, value, icon, trend, percentage, color }) => (
    <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
      <div className="flex justify-between items-center">
        <div>
          <p className="text-sm text-gray-500 mb-1">{title}</p>
          <h3 className={`text-2xl font-bold mb-2 text-${color}-600`}>{value}</h3>
          <div className="flex items-center">
            {/* {trend === 'up' ? (
              <FiArrowUp className="text-blue-500" />
            ) : (
              <FiArrowDown className="text-red-500" />
            )} */}
            {/* <span className={`ml-2 text-sm ${trend === 'up' ? 'text-blue-500' : 'text-red-500'}`}>
              {percentage}
            </span> */}
          </div>
        </div>
        <div className={`p-4 rounded-full bg-${color}-100`}>
          {React.cloneElement(icon, { className: `text-2xl text-${color}-600` })}
        </div>
      </div>
    </div>
  );

  const RecentTransaction = ({ transaction }) => (
    <div className="flex justify-between items-center p-4 hover:bg-gray-50 rounded-lg transition-colors duration-200">
      <div>
        <p className="font-medium">{transaction.description}</p>
        <p className="text-sm text-gray-500">
          {new Date(transaction.date).toLocaleDateString()}
        </p>
      </div>
      <span className={`font-semibold ${transaction.type === 'income' ? 'text-blue-600' : 'text-red-600'}`}>
        {formatToZMW(transaction.amount)}
      </span>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-7xl mx-auto">
          <Skeleton count={6} height={100} className="mb-4" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        <header className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Dashboard</h1>
            <p className="text-gray-500">Welcome back, {auth.user?.email}</p>
          </div>
          {/* <DatePicker
            selectsRange
            startDate={startDate}
            endDate={endDate}
            onChange={(update) => setDateRange(update)}
            className="border rounded-lg px-4 py-2 shadow-sm"
          /> */}
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <KpiCard
            title="Total Receipts"
            value={formatToZMW(sumOfArray(dash.filter(a => a.type === 'income').map(res => parseFloat(res.amount))))}
            icon={<FiDollarSign />}
            // trend="up"
            // percentage="+12% from last month"
            
            color="blue"
          />
          <KpiCard
            title="Total Payments"
            value={formatToZMW(sumOfArray(dash.filter(a => a.type === 'expense').map(res => parseFloat(res.amount))))}
            icon={<FiTrendingUp />}
            // trend="down"
            // percentage="-5% from last month"
            color="green"
          />
          <KpiCard
            title="Net Balance"
            value={formatToZMW(
              sumOfArray(dash.filter(a => a.type === 'income').map(res => parseFloat(res.amount))) -
              sumOfArray(dash.filter(a => a.type === 'expense').map(res => parseFloat(res.amount)))
            )}
            icon={<FiActivity />}
            // trend="up"
            // percentage="+7% from last month"
            color="purple"
          />
          <KpiCard
            title="Avg. Weekly Profit"
            value={formatToZMW(cht.reduce((acc, curr) => acc + curr.netProfit, 0) / (cht.length || 1))}
            icon={<FiArrowUp />}
            // trend="up"
            // percentage="+15% from last month"
            color="indigo"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <h2 className="text-xl font-semibold mb-4">Financial Overview</h2>
            <div className="h-80">
              
              <Bar
                data={invoiceData}
                options={chartOptions}
                redraw
              />
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-lg">
            <h2 className="text-xl font-semibold mb-4">Cash Flow</h2>
            <div className="h-80">
              <Line
                data={paymentsData}
                options={chartOptions}
                redraw
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <h2 className="text-xl font-semibold mb-4">Income/Expsense Ratio</h2>
            <div className="h-64">
              <Pie
                data={expenseData}
                options={{
                  ...chartOptions,
                  plugins: {
                    tooltip: {
                      callbacks: {
                        label: (context) => ` ${context.label}: ${formatToZMW(context.raw)}`
                      }
                    }
                  }
                }}
              />
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-lg lg:col-span-2">
            <h2 className="text-xl font-semibold mb-4">Net Profit Trend</h2>
            <div className="h-64">
              <Bar
                data={netProfitData}
                options={chartOptions}
                redraw
              />
            </div>
          </div>
        </div>

        {/* <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Recent Transactions</h2>
          <div className="space-y-2">
            {dash.slice(0, 8).map((transaction, index) => (
              <RecentTransaction key={index} transaction={transaction} />
            ))}
            {dash.length === 0 && (
              <p className="text-center text-gray-500 py-4">No transactions found</p>
            )}
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default Dashboards;